import React, { useState } from 'react';
import { 
  PlayIcon, 
  DocumentDuplicateIcon,
  ChevronRightIcon,
  TagIcon,
  GlobeAltIcon,
  ShoppingCartIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { getRequestTemplates, RequestTemplate } from '../../services/apiPlaygroundService';

interface ApiTemplatesProps {
  onTemplateSelect: (template: RequestTemplate) => void;
}

const ApiTemplates: React.FC<ApiTemplatesProps> = ({ onTemplateSelect }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
  
  const templates = getRequestTemplates();
  const categories = ['All', ...Array.from(new Set(templates.map(t => t.category)))];

  const filteredTemplates = selectedCategory === 'All' 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Scraping': return GlobeAltIcon;
      case 'E-commerce': return ShoppingCartIcon;
      case 'Advanced': return MagnifyingGlassIcon;
      case 'Account': return UserGroupIcon;
      case 'Testing': return CogIcon;
      default: return TagIcon;
    }
  };

  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET': return 'bg-green-100 text-green-800';
      case 'POST': return 'bg-blue-100 text-blue-800';
      case 'PUT': return 'bg-yellow-100 text-yellow-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Request Templates</h2>
          <p className="text-sm text-gray-500">
            Pre-built examples for common use cases
          </p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              selectedCategory === category
                ? 'bg-blue-100 text-blue-800 border border-blue-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Templates Grid */}
      <div className="grid gap-4">
        {filteredTemplates.map((template) => {
          const CategoryIcon = getCategoryIcon(template.category);
          const isExpanded = expandedTemplate === template.id;
          
          return (
            <div
              key={template.id}
              className="bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
            >
              {/* Template Header */}
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <CategoryIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="text-sm font-medium text-gray-900">
                          {template.name}
                        </h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getMethodColor(template.method)}`}>
                          {template.method}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">
                        {template.description}
                      </p>
                      <div className="flex items-center text-xs text-gray-400">
                        <span className="bg-gray-100 px-2 py-1 rounded">
                          {template.category}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setExpandedTemplate(isExpanded ? null : template.id)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <ChevronRightIcon 
                        className={`h-5 w-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                      />
                    </button>
                    <button
                      onClick={() => onTemplateSelect(template)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <PlayIcon className="h-4 w-4 mr-1" />
                      Use
                    </button>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="border-t border-gray-200 bg-gray-50">
                  <div className="p-4 space-y-4">
                    {/* Endpoint */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Endpoint
                      </label>
                      <div className="flex items-center space-x-2">
                        <code className="flex-1 text-sm bg-white border border-gray-200 rounded px-2 py-1 font-mono">
                          {template.method} {template.endpoint}
                        </code>
                        <button
                          onClick={() => copyToClipboard(`${template.method} ${template.endpoint}`)}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <DocumentDuplicateIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Request Body */}
                    {template.body && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Request Body
                        </label>
                        <div className="relative">
                          <pre className="text-xs bg-white border border-gray-200 rounded p-2 overflow-x-auto font-mono">
                            {JSON.stringify(template.body, null, 2)}
                          </pre>
                          <button
                            onClick={() => copyToClipboard(JSON.stringify(template.body, null, 2))}
                            className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 bg-white rounded"
                          >
                            <DocumentDuplicateIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Example Response */}
                    {template.example_response && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Example Response
                        </label>
                        <div className="relative">
                          <pre className="text-xs bg-white border border-gray-200 rounded p-2 overflow-x-auto font-mono">
                            {JSON.stringify(template.example_response, null, 2)}
                          </pre>
                          <button
                            onClick={() => copyToClipboard(JSON.stringify(template.example_response, null, 2))}
                            className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 bg-white rounded"
                          >
                            <DocumentDuplicateIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Headers */}
                    {Object.keys(template.headers).length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Headers
                        </label>
                        <div className="space-y-1">
                          {Object.entries(template.headers).map(([key, value]) => (
                            <div key={key} className="flex items-center space-x-2 text-xs">
                              <span className="font-medium text-gray-600">{key}:</span>
                              <span className="text-gray-800">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <TagIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No templates found</h3>
          <p className="mt-1 text-sm text-gray-500">
            No templates match the selected category.
          </p>
        </div>
      )}
    </div>
  );
};

export default ApiTemplates;
